import { create } from 'zustand';

const STORAGE_KEY = 'waterlily:pdfReportSections';

type PersistedPdfReportSections = {
  includeCareJourney: boolean;
  includePredictedCareNeeds: boolean;
};

interface PdfReportSectionsState {
  includeCareJourney: boolean;
  includePredictedCareNeeds: boolean;
  hydrate: () => void;
  toggleCareJourney: () => void;
  togglePredictedCareNeeds: () => void;
}

function safeParseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function persistSections(state: PersistedPdfReportSections) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // If localStorage is blocked/unavailable, we simply fall back to in-memory state.
  }
}

export const usePdfReportSections = create<PdfReportSectionsState>((set, get) => ({
  includeCareJourney: true,
  includePredictedCareNeeds: true,
  hydrate: () => {
    const persisted = safeParseJson<PersistedPdfReportSections>(
      typeof window === 'undefined' ? null : localStorage.getItem(STORAGE_KEY),
    );

    if (!persisted) {
      return;
    }

    set({
      includeCareJourney: persisted.includeCareJourney ?? true,
      includePredictedCareNeeds: persisted.includePredictedCareNeeds ?? true,
    });
  },
  toggleCareJourney: () =>
    set(state => {
      const next = !state.includeCareJourney;
      persistSections({
        includeCareJourney: next,
        includePredictedCareNeeds: state.includePredictedCareNeeds,
      });
      return { includeCareJourney: next };
    }),
  togglePredictedCareNeeds: () =>
    set(state => {
      const next = !state.includePredictedCareNeeds;
      persistSections({
        includeCareJourney: state.includeCareJourney,
        includePredictedCareNeeds: next,
      });
      return { includePredictedCareNeeds: next };
    }),
}));
